import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_AUTH_BACKEND_URL;

/**
 * Circuit-breaker (agentic reliability) state for the user's models.
 *
 * A model is "degraded" after repeated structured tool-calling
 * failures: expansive/evaluation runs use simplified deterministic
 * routing for it until the periodic probe (or a manual reset here)
 * re-enables full agent mode.
 *
 * @returns {{
 *   reliability: Array<{provider: string, model_name: string,
 *     consecutive_failures: number, last_attempt_at: ?string,
 *     degraded: boolean}>,
 *   degradedModels: Array<object>,
 *   loading: boolean,
 *   refresh: Function,
 *   resetModel: Function,
 * }}
 */
export default function useModelReliability() {
  const [reliability, setReliability] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/user/model-reliability`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReliability(Array.isArray(data.models) ? data.models : []);
    } catch (err) {
      // Non-fatal: the panel simply stays empty.
      console.error('Failed to fetch model reliability:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const resetModel = useCallback(async (provider, modelName) => {
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/user/model-reliability/reset`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            provider,
            model_name: modelName,
          }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(
        `Full agent mode re-enabled for ${modelName}.`,
      );
      await refresh();
    } catch (err) {
      console.error('Failed to reset model reliability:', err);
      toast.error('Failed to re-enable full agent mode.');
    }
  }, [refresh]);

  const degradedModels = reliability.filter((m) => m.degraded);

  return { reliability, degradedModels, loading, refresh, resetModel };
}
